import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { ISupportConversation } from 'app/types/support.interface';
import { HydraApiResponse } from 'app/types/hydraData';
import { z3xBaseQuery } from 'app/utils/api.utils';


interface IGetConversationsArgs {
    page: number;
    itemsPerPage: number;
}

interface IGetConversationsResponse {
    conversations: ISupportConversation[]
    total: number
}

interface ISendMessageArgs {
    body: string;
    conversationId: number;
    attachments: number[];
}

interface IAttachmentResponse {
    id: number;
    fileName: string;
    mimeType: string;
    fileSize: number;
    expireAt: string;
}

export const conversationApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery: z3xBaseQuery({ type: 'ld+json' }),
  tagTypes: ['Conversation'],
  endpoints: (builder) => ({
    getConversations: builder.query<IGetConversationsResponse, IGetConversationsArgs>({
      query: ({ page, itemsPerPage }) => ({
        method: 'GET',
        url: '/conversations',
        params: { page, itemsPerPage }
      }),
      transformResponse: (response: HydraApiResponse<ISupportConversation[]>) => {
        return {
          conversations: response['hydra:member'],
          total: response['hydra:totalItems']
        };
      },
      providesTags: ['Conversation'],
      keepUnusedDataFor: 0
    }),
    getConversation: builder.query<ISupportConversation, string>({
      query: (id) => ({
        method: 'GET',
        url: `/conversations/${id}`
      }),
      providesTags: (result, error, id) => [{ type: 'Conversation', id }],
      keepUnusedDataFor: 0
    }),
    sendMessage: builder.mutation<null, ISendMessageArgs>({
      query: ({ body, conversationId, attachments }) => ({
        method: 'POST',
        url: '/threads',
        body: {
          body,
          conversationId,
          attachments
        }
      }),
      invalidatesTags: (result, error, { conversationId }) => [
        { type: 'Conversation', id: conversationId.toString() }
      ]
    }),
    uploadAttachment: builder.mutation<IAttachmentResponse, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/attachments',
          method: 'POST',
          body: formData,
        };
      },
    }),
    deleteAttachment: builder.mutation<void, number>({
      query: (id) => ({
        url: `/attachments/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetConversationsQuery,
  useGetConversationQuery,
  useSendMessageMutation,
  useUploadAttachmentMutation,
  useDeleteAttachmentMutation
} = conversationApi;
